import type { UciMove } from '../api/types';
import type { BoardArrow } from './chessground';

export interface PreparedMove {
  uci: UciMove;
  role: 'main' | 'alternative' | 'opponent';
}

/**
 * Board overlay for a game-review moment, drawn on the position BEFORE the move:
 * - a played move outside the repertoire is red ("Played");
 * - prepared moves are green (main line / prepared opponent replies) or blue
 *   (alternatives), so the divergence reads at a glance;
 * - a played move that IS prepared is drawn once in its role colour, marked ✓;
 * - a transposition's played move is green: it is what brought the game back in.
 */
export function momentArrows(
  played: UciMove,
  prepared: readonly PreparedMove[],
  options: { transposition?: boolean } = {},
): BoardArrow[] {
  const brush = (role: PreparedMove['role']): BoardArrow['brush'] => (role === 'alternative' ? 'blue' : 'green');
  const seen = new Set<string>();
  const arrows: BoardArrow[] = [];
  const playedPrepared = prepared.find((move) => move.uci === played);
  if (options.transposition) {
    arrows.push({ uci: played, brush: 'green', label: '↩' });
    seen.add(played);
  } else if (playedPrepared) {
    arrows.push({ uci: played, brush: brush(playedPrepared.role), label: '✓' });
    seen.add(played);
  } else {
    arrows.push({ uci: played, brush: 'red', label: '✗' });
    seen.add(played);
  }
  for (const move of prepared) {
    if (seen.has(move.uci)) continue;
    seen.add(move.uci);
    arrows.push({ uci: move.uci, brush: brush(move.role) });
  }
  return arrows;
}
